import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { WidgetConfigurationService } from '../../../@backoffice/services/widget-configuration.service';
import { normalizePartialViewName } from '../../../@backoffice/utils/partial-view-config.util';
import { buildClientRoute } from '../utils/client-route.util';

/**
 * Partial View Active Guard
 *
 * Blocks a route whose partial view has been made Inactive in Widget Configuration (partial view
 * Config → DisplayStatus). Sends the user back to the member's default tab, which skips inactive tabs.
 * Fails open when the partial view list can't be loaded.
 *
 * Usage: `canActivate: [permissionGuard(...), partialViewActiveGuard('MemberDetails', 'Account Info')]`
 */
export const partialViewActiveGuard = (viewName: string, partialViewName: string): CanActivateFn => {
  return (route) => {
    const widgetService = inject(WidgetConfigurationService);
    const router = inject(Router);

    return widgetService.getInactivePartialViewNames(viewName).pipe(
      map((inactive) => {
        if (!inactive.has(normalizePartialViewName(partialViewName))) {
          return true;
        }
        const memberId = route.parent?.paramMap.get('id') ?? route.paramMap.get('id');
        void router.navigate(buildClientRoute(router, memberId ? ['/members', memberId] : ['/members/search']));
        return false;
      })
    );
  };
};
